import {useCookieStorage} from '@github-ui/use-safe-storage/cookie-storage'
import {use, useCallback, useEffect} from 'react'

import {LayoutBehaviorContext} from '../layout-runtime'

/** Site-wide cookie containing all sidebar collapse states. */
export const SIDEBAR_STATE_COOKIE_KEY = 'primer_sidebar_expanded'

/** Default sub-key for surfaces that intentionally share one collapse state. */
export const GLOBAL_SIDEBAR_STORAGE_KEY = 'global'

export type SidebarState = Record<string, boolean>

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function toSidebarState(value: unknown): SidebarState {
  if (!isRecord(value)) return {}

  return Object.fromEntries(
    Object.entries(value).filter((entry): entry is [string, boolean] => typeof entry[1] === 'boolean'),
  )
}

function getOwnBoolean(value: unknown, key: string): boolean | undefined {
  if (!isRecord(value) || !Object.hasOwn(value, key)) return undefined
  return typeof value[key] === 'boolean' ? value[key] : undefined
}

function getExpanded(stored: unknown, storageKey: string, legacyExpanded: unknown, fallback: boolean): boolean {
  const storedExpanded = getOwnBoolean(stored, storageKey)
  if (storedExpanded !== undefined) return storedExpanded
  if (storageKey === GLOBAL_SIDEBAR_STORAGE_KEY && typeof stored === 'boolean') {
    return stored
  }
  if (typeof legacyExpanded === 'boolean') return legacyExpanded
  return fallback
}

function getNextState(stored: unknown, storageKey: string, expanded: boolean): SidebarState {
  return {
    ...toSidebarState(stored),
    ...(typeof stored === 'boolean' ? {[GLOBAL_SIDEBAR_STORAGE_KEY]: stored} : {}),
    [storageKey]: expanded,
  }
}

function removeStoredState(stored: unknown, storageKey: string): SidebarState {
  const state = toSidebarState(stored)
  delete state[storageKey]
  return state
}

export interface UseCollapsibleSidebarStateOptions {
  /**
   * The persistence key for this sidebar. Defaults to
   * {@link GLOBAL_SIDEBAR_STORAGE_KEY}; pass a custom key when a surface needs
   * independent collapse state. The hook handles both legacy standalone
   * cookies and the shared {@link SIDEBAR_STATE_COOKIE_KEY} format.
   */
  storageKey?: string
  defaultExpanded?: boolean
  /**
   * Server-read seed so SSR markup matches the client's first paint (prevents
   * the flash on hard navigation). Falls back to `defaultExpanded`.
   */
  initialExpanded?: boolean
}

export interface CollapsibleSidebarState {
  isExpanded: boolean
  toggle: () => void
  setExpanded: (expanded: boolean | ((prev: boolean) => boolean)) => void
}

/**
 * Cookie-backed, SSR-safe expanded/collapsed state. Seed the first render with
 * `initialExpanded` (the server-read cookie value) to avoid a flash on hard
 * navigation.
 */
export function useCollapsibleSidebarState(
  {
    storageKey = GLOBAL_SIDEBAR_STORAGE_KEY,
    defaultExpanded = true,
    initialExpanded,
  }: UseCollapsibleSidebarStateOptions = {},
  sharedCookieEnabledOverride?: boolean,
): CollapsibleSidebarState {
  const {sharedCookieEnabled: providerSharedCookieEnabled} = use(LayoutBehaviorContext)
  const sharedCookieEnabled = sharedCookieEnabledOverride ?? providerSharedCookieEnabled
  const resolvedStorageKey = storageKey === SIDEBAR_STATE_COOKIE_KEY ? GLOBAL_SIDEBAR_STORAGE_KEY : storageKey
  const seed = initialExpanded ?? defaultExpanded
  const [storedState, setStoredState] = useCookieStorage<unknown>(SIDEBAR_STATE_COOKIE_KEY, undefined)
  const [legacyExpanded, setLegacyExpanded] = useCookieStorage<unknown>(resolvedStorageKey, undefined)
  const isExpanded = getExpanded(storedState, resolvedStorageKey, legacyExpanded, seed)

  useEffect(() => {
    if (sharedCookieEnabled) {
      if (
        getOwnBoolean(storedState, resolvedStorageKey) === undefined &&
        (typeof storedState === 'boolean' || typeof legacyExpanded === 'boolean')
      ) {
        setStoredState((stored: unknown) => getNextState(stored, resolvedStorageKey, isExpanded))
      }
    } else if (resolvedStorageKey === GLOBAL_SIDEBAR_STORAGE_KEY) {
      if (
        typeof storedState !== 'boolean' &&
        (getOwnBoolean(storedState, resolvedStorageKey) !== undefined || typeof legacyExpanded === 'boolean')
      ) {
        setStoredState(isExpanded)
      }
    } else {
      const sharedExpanded = getOwnBoolean(storedState, resolvedStorageKey)
      if (sharedExpanded !== undefined) {
        setLegacyExpanded(sharedExpanded)
        setStoredState((stored: unknown) => removeStoredState(stored, resolvedStorageKey))
      }
    }
  }, [
    isExpanded,
    legacyExpanded,
    resolvedStorageKey,
    setLegacyExpanded,
    setStoredState,
    sharedCookieEnabled,
    storedState,
  ])

  const setExpanded = useCallback(
    (expanded: boolean | ((prev: boolean) => boolean)) => {
      if (sharedCookieEnabled || resolvedStorageKey === GLOBAL_SIDEBAR_STORAGE_KEY) {
        setStoredState((stored: unknown) => {
          const current = getExpanded(stored, resolvedStorageKey, legacyExpanded, seed)
          const resolved = typeof expanded === 'function' ? expanded(current) : expanded
          return sharedCookieEnabled ? getNextState(stored, resolvedStorageKey, resolved) : resolved
        })
      } else {
        setLegacyExpanded((stored: unknown) => {
          const current = typeof stored === 'boolean' ? stored : seed
          return typeof expanded === 'function' ? expanded(current) : expanded
        })
      }
    },
    [legacyExpanded, resolvedStorageKey, seed, setLegacyExpanded, setStoredState, sharedCookieEnabled],
  )

  const toggle = useCallback(() => {
    setExpanded(prev => !prev)
  }, [setExpanded])

  return {isExpanded, toggle, setExpanded}
}

export function useLayoutSidebarState(options: UseCollapsibleSidebarStateOptions): CollapsibleSidebarState {
  const {sharedCookieEnabled} = use(LayoutBehaviorContext)
  return useCollapsibleSidebarState(options, sharedCookieEnabled)
}
